import React from 'react';
import { EMPTY_COLUMN_HINT, ITEM_DRAG_TYPE, NOT_BILLABLE_NOTE } from '../constants';
import { ColumnError, ColumnHeader, ColumnMeta, ColumnName, ColumnTitleRow, DropHint, NotBillableNote } from './blocks';
import { DropColumn } from './DropColumn';

/**
 * Dashed drop hint for a new-order column with no cards in the current mode.
 *
 * @param {{ text?: string }} props `text` defaults to "Drag items here".
 */
export const EmptyColumnHint = ({ text = EMPTY_COLUMN_HINT }) => <DropHint>{text}</DropHint>;

/**
 * One new-order column on the split board, and a drop target for the current mode's cards. The header shows the
 * order number it will get and its counts; `actions` sit to the right of the name (the Billable switch and remove
 * control). `summary` sits above the cards (the collapsed item summary in services mode). When `empty` it shows
 * the drop hint. A column that isn't billable says so under its header, and `error` shows a problem at its foot.
 *
 * @param {{ orderKey: string, label: string, meta: string, empty: boolean, notBillable?: boolean, error?: string|null, type?: string, hint?: string, summary?: React.ReactNode, actions?: React.ReactNode, disabled?: boolean, children?: React.ReactNode }} props
 *   `orderKey` is the column's layout key, used as its drop id. `meta` is the count line from `columnSummary`.
 *   `empty` is true when the column holds no cards of the current mode. `type` is the drag type it accepts and
 *   `hint` the empty drop hint for that mode. `notBillable` shows the not-billable note. `error`
 *   is the column's problem from `columnProblems`, if any. `disabled` stops drops.
 */
export const OrderColumn = ({
    orderKey,
    label,
    meta,
    empty,
    notBillable = false,
    error = null,
    type = ITEM_DRAG_TYPE,
    hint = EMPTY_COLUMN_HINT,
    summary = null,
    actions = null,
    disabled = false,
    children,
}) => (
    <DropColumn
        droppableId={orderKey}
        type={type}
        disabled={disabled}
        aria-label={label}
        data-testid={`split-board-column-${label}`}
        invalid={Boolean(error)}
        footer={error ? <ColumnError>{error}</ColumnError> : null}
        header={
            <ColumnHeader>
                <ColumnTitleRow>
                    <ColumnName>{label}</ColumnName>
                    {actions}
                </ColumnTitleRow>
                <ColumnMeta>{meta}</ColumnMeta>
                {notBillable ? <NotBillableNote>{NOT_BILLABLE_NOTE}</NotBillableNote> : null}
            </ColumnHeader>
        }
    >
        {summary}
        {empty ? <EmptyColumnHint text={hint} /> : children}
    </DropColumn>
);

export default OrderColumn;
